import { Switch, Route, Redirect } from "wouter";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Preloader } from "@/components/shared/Preloader";
import NotFound from "@/pages/not-found";
// Eager on purpose — see the code-splitting note below.
import Home from "@/pages/home";

/**
 * Route-level code splitting.
 *
 * Every page used to be a static import, so a visitor landing on `/` downloaded,
 * parsed and executed the whole product: the admin panel (dnd-kit, Tiptap,
 * TinyMCE), the comparison tables, the quiz engine, the dashboards — none of
 * which the homepage renders. The mobile run measured 937.9 KiB of first-party
 * JS with 593.8 KiB of it unused on the critical path, and that single blocking
 * bundle is what pushed FCP to 2.7 s and Speed Index to 7.8 s.
 *
 * `Home` deliberately stays a static import. It is the landing route for the
 * overwhelming majority of entries, so deferring it would trade a smaller bundle
 * for an extra round trip *in front of the LCP element* — the wrong direction.
 * Everything else is fetched on first navigation to its route, which Vite turns
 * into one small chunk per page.
 *
 * Two rules for anyone adding a route:
 *
 *  1. `lazy()` needs a *default* export. Every page in client/src/pages has one;
 *     keep it that way, or wrap with `{ default: X }` at the call site.
 *  2. The `import()` argument must be a literal path for Vite to analyse it.
 */
const Brokers = lazy(() => import("@/pages/brokers"));
const BrokerComparison = lazy(() => import("@/pages/broker-comparison"));
const BrokerDetail = lazy(() => import("@/pages/broker-detail"));
const VPS = lazy(() => import("@/pages/vps"));
const EAs = lazy(() => import("@/pages/eas"));
const EADetail = lazy(() => import("@/pages/ea-detail"));
const Bonuses = lazy(() => import("@/pages/bonuses"));
const BonusDetailPage = lazy(() => import("@/pages/bonus-detail"));
const News = lazy(() => import("@/pages/news"));
const NewsDetail = lazy(() => import("@/pages/news-detail"));
const NewsCategoryPage = lazy(() => import("@/pages/news-category"));
const NewsTagPage = lazy(() => import("@/pages/news-tag"));
const NewsAuthorPage = lazy(() => import("@/pages/news-author"));
const Analysis = lazy(() => import("@/pages/analysis"));
const Education = lazy(() => import("@/pages/education"));
const Quizzes = lazy(() => import("@/pages/quizzes"));
const CryptoQuizzes = lazy(() => import("@/pages/crypto-quizzes"));
const Forexpedia = lazy(() => import("@/pages/forexpedia"));
const Register = lazy(() => import("@/pages/register"));
const Login = lazy(() => import("@/pages/login"));
const Auth = lazy(() => import("@/pages/auth"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const DashboardProfile = lazy(() => import("@/pages/dashboard-profile"));
const DashboardReviews = lazy(() => import("@/pages/dashboard-reviews"));
const DashboardComplaints = lazy(() => import("@/pages/dashboard-complaints"));
const DashboardMessages = lazy(() => import("@/pages/dashboard-messages"));
const DashboardKyc = lazy(() => import("@/pages/dashboard-kyc"));
const Admin = lazy(() => import("@/pages/admin"));
const Calculators = lazy(() => import("@/pages/calculators"));
const Crypto = lazy(() => import("@/pages/crypto"));
const CryptoExchangeDetail = lazy(() => import("@/pages/crypto-exchange-detail"));
const CryptoComparison = lazy(() => import("@/pages/crypto-comparison"));
const EconomicCalendar = lazy(() => import("@/pages/economic-calendar"));
const GlobalCoverageMap = lazy(() => import("@/pages/global-coverage-map"));
const NewsDigest = lazy(() => import("@/pages/news-digest"));
const IntelligenceHub = lazy(() => import("@/pages/intelligence-hub"));
const Contact = lazy(() => import("@/pages/contact"));
const LegalPage = lazy(() => import("@/pages/legal-page"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const VerifyEmail = lazy(() => import("@/pages/verify-email"));
const AcceptInvite = lazy(() => import("@/pages/accept-invite"));
const Complaints = lazy(() => import("@/pages/complaints"));
const ComplaintDetailPage = lazy(() => import("@/pages/complaint-detail"));
const ComplaintNew = lazy(() => import("@/pages/complaint-new"));
const CryptoComplaints = lazy(() => import("@/pages/crypto-complaints"));
const CryptoComplaintDetailPage = lazy(() => import("@/pages/crypto-complaint-detail"));
const CryptoComplaintNew = lazy(() => import("@/pages/crypto-complaint-new"));
const PublicUserProfile = lazy(() => import("@/pages/public-user-profile"));

type PublicRuntimeSeo = {
  preloaderEnabled?: boolean | null;
  faviconUrl?: string | null;
  /** Display name, used for the preloader's initials badge when there is no icon. */
  siteName?: string | null;
  maintenance?: {
    enabled?: boolean;
    title?: string | null;
    message?: string | null;
  } | null;
};

type MaintenanceAuthMe = {
  auth?: {
    permissions?: string[] | null;
  } | null;
};

function MaintenanceScreen({ title, message }: { title: string; message: string }) {
  return (
    <div className="min-h-screen bg-[#0a101f] text-white px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
        <div className="w-full overflow-hidden rounded-[2rem] border border-white/10 bg-[#10182a]/90 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="space-y-5">
              <div className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-primary">
                Maintenance Mode
              </div>
              <div className="text-[clamp(4rem,16vw,7rem)] font-black leading-none tracking-[-0.06em] text-white/10">503</div>
              <h1 className="text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">{title}</h1>
              <p className="max-w-2xl text-sm leading-7 text-gray-400 sm:text-base">{message}</p>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button className="h-12 rounded-xl bg-primary px-6 font-black text-black hover:bg-primary/90" onClick={() => window.location.reload()}>
                  Refresh Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Placeholder shown while a lazily-loaded route chunk is in flight.
 *
 * It is a plain dark block with no text and no image on purpose: it only ever
 * appears on a *navigation* (never on the first paint — that is `Home`, which is
 * eager), it must not itself become a paint the browser has to undo, and the
 * page background is already #0a101f, so it reads as a continuous surface rather
 * than a flash.
 */
function RouteChunkFallback() {
  return <div className="min-h-[70vh] bg-[#0a101f]" />;
}

function Router() {
  const [location] = useLocation();
  const maintenanceQuery = useQuery<PublicRuntimeSeo>({
    queryKey: ["/api/seo", "maintenance"],
    queryFn: async () => {
      const res = await fetch("/api/seo", { cache: "no-store" });
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 0,
    refetchInterval: 15_000,
    retry: false,
  });

  const maintenance = maintenanceQuery.data?.maintenance;
  const maintenanceEnabled = !!maintenance?.enabled;
  const adminAuthQuery = useQuery<MaintenanceAuthMe>({
    queryKey: ["/api/auth/me", "maintenance"],
    queryFn: async () => {
      const res = await fetch("/api/auth/me", { credentials: "include", cache: "no-store" });
      if (res.status === 401) return {};
      if (!res.ok) return {};
      return res.json();
    },
    enabled: maintenanceEnabled,
    staleTime: 0,
    refetchInterval: 15_000,
    retry: false,
  });

  const isAdminDuringMaintenance = !!adminAuthQuery.data?.auth?.permissions?.length;
  const maintenanceAllowedRoute =
    location.startsWith("/admin") ||
    location === "/login" ||
    location === "/auth" ||
    location.startsWith("/auth?") ||
    location.startsWith("/register") ||
    location.startsWith("/forgot-password") ||
    location.startsWith("/reset-password") ||
    location.startsWith("/verify-email") ||
    location.startsWith("/accept-invite");

  useEffect(() => {
    // Don't force-scroll inside the admin shell (it has its own scroll container).
    if (location.startsWith("/admin")) return;
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (!maintenanceEnabled) return;
    const robotsMeta = document.head.querySelector('meta[name="robots"]') || document.createElement("meta");
    robotsMeta.setAttribute("name", "robots");
    robotsMeta.setAttribute("content", "noindex, nofollow, noarchive");
    if (!robotsMeta.parentElement) document.head.appendChild(robotsMeta);
  }, [maintenanceEnabled]);

  if (maintenanceEnabled && !maintenanceAllowedRoute && adminAuthQuery.isLoading) {
    return <div className="min-h-screen bg-[#0a101f]" />;
  }

  if (maintenanceEnabled && !maintenanceAllowedRoute && !isAdminDuringMaintenance) {
    return (
      <MaintenanceScreen
        title={String(maintenance?.title || "Scheduled platform maintenance")}
        message={String(maintenance?.message || "We're performing a brief infrastructure update. Please check back shortly.")}
      />
    );
  }

  return (
    <Suspense fallback={<RouteChunkFallback />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/brokers" component={Brokers} />
        <Route path="/broker-comparison" component={BrokerComparison} />
        <Route path="/broker/:id" component={BrokerDetail} />
        <Route path="/vps" component={VPS} />
        <Route path="/eas" component={EAs} />
        <Route path="/ea/:id" component={EADetail} />
        <Route path="/bonuses" component={Bonuses} />
        <Route path="/bonuses/:slug" component={BonusDetailPage} />
        <Route path="/news" component={News} />
        <Route path="/news/category/:slug" component={NewsCategoryPage} />
        <Route path="/news/tag/:slug" component={NewsTagPage} />
        <Route path="/news/author/:slug" component={NewsAuthorPage} />
        <Route path="/analysis" component={Analysis} />
        <Route path="/education" component={Education} />
        <Route path="/quizzes" component={Quizzes} />
        <Route path="/crypto-quizzes" component={CryptoQuizzes} />
        <Route path="/forexpedia" component={Forexpedia} />
        <Route path="/auth" component={Auth} />
        <Route path="/register" component={Register} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password/:token" component={ResetPassword} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/accept-invite/:token" component={AcceptInvite} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/dashboard/profile" component={DashboardProfile} />
        <Route path="/dashboard/reviews" component={DashboardReviews} />
        <Route path="/dashboard/complaints" component={DashboardComplaints} />
        <Route path="/dashboard/messages" component={DashboardMessages} />
        <Route path="/dashboard/kyc" component={DashboardKyc} />
        {/* Admin routes (wouter wildcard matching differs by build); enumerate common depths */}
        <Route path="/admin" component={Admin} />
        <Route path="/admin/:a" component={Admin} />
        <Route path="/admin/:a/:b" component={Admin} />
        <Route path="/admin/:a/:b/:c" component={Admin} />
        <Route path="/admin/:a/:b/:c/:d" component={Admin} />
        <Route path="/admin/:a/:b/:c/:d/:e" component={Admin} />
        <Route path="/calculators" component={Calculators} />
        <Route path="/crypto" component={Crypto} />
        <Route path="/crypto/compare" component={CryptoComparison} />
        <Route path="/crypto/:slug" component={CryptoExchangeDetail} />
        <Route path="/news/:slug" component={NewsDetail} />
        <Route path="/tools/economic-calendar" component={EconomicCalendar} />
        <Route path="/tools/global-coverage" component={GlobalCoverageMap} />
        <Route path="/tools/global-coverage-map" component={GlobalCoverageMap} />
        <Route path="/tools/news-digest" component={NewsDigest} />
        <Route path="/tools/ai-news-digest" component={NewsDigest} />
        <Route path="/intelligence-hub" component={IntelligenceHub} />
        <Route path="/markets-insight" component={IntelligenceHub} />
        <Route path="/tools/intelligence-hub" component={IntelligenceHub} />
        <Route path="/complaints/new/:brokerSlug" component={ComplaintNew} />
        <Route path="/complaints" component={Complaints} />
        <Route path="/complaints/:id/:brokerSlug/:titleSlug" component={ComplaintDetailPage} />
        <Route path="/complaints/:id" component={ComplaintDetailPage} />
        <Route path="/crypto-complaints/new/:exchangeSlug" component={CryptoComplaintNew} />
        <Route path="/crypto-complaints" component={CryptoComplaints} />
        <Route path="/crypto-complaints/:id/:exchangeSlug/:titleSlug" component={CryptoComplaintDetailPage} />
        <Route path="/crypto-complaints/:id" component={CryptoComplaintDetailPage} />
        <Route path="/user/:id" component={PublicUserProfile} />
        <Route path="/contact" component={Contact} />
        {/*
          Legal / trust pages. Content is managed at Admin → Content → Pages and
          rendered server-side for crawlers by the SEO snapshot middleware, so a
          copy change is visible to non-JS clients immediately.

          These paths matter for the migration too: the previous site served
          `/privacy-policy` and `/terms-conditions`, and `/about-us` was indexed.
          `/about-us` and `/terms-conditions` 301 here (see BUILTIN_LEGACY_REDIRECTS
          and the seeded `redirects` rows).
        */}
        <Route path="/about" component={() => <LegalPage slug="about" />} />
        <Route path="/privacy-policy" component={() => <LegalPage slug="privacy-policy" />} />
        <Route path="/terms" component={() => <LegalPage slug="terms" />} />
        <Route path="/terms-conditions" component={() => <Redirect to="/terms" />} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as any;
    if (w.__fx_fetch_locale_patched) return;
    w.__fx_fetch_locale_patched = true;

    const origFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : (input as any)?.url || "";

        const isApi = typeof url === "string" && url.startsWith("/api/");
        if (!isApi) return origFetch(input as any, init);

        const locale = (() => {
          try {
            return (localStorage.getItem("fx.locale") || "").trim();
          } catch {
            return "";
          }
        })();
        if (!locale) return origFetch(input as any, init);

        // If input is a Request and init is undefined, clone to attach headers.
        if (input instanceof Request && !init) {
          const headers = new Headers(input.headers);
          if (!headers.has("X-Locale")) headers.set("X-Locale", locale);
          return origFetch(new Request(input, { headers }) as any);
        }

        const headers = new Headers((init as any)?.headers || (input instanceof Request ? input.headers : undefined));
        if (!headers.has("X-Locale")) headers.set("X-Locale", locale);
        return origFetch(input as any, { ...(init || {}), headers });
      } catch {
        return origFetch(input as any, init);
      }
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AppShell />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

/**
 * Minimum time the preloader stays up, purely so a warm cache doesn't produce a
 * one-frame flash of the overlay.
 */
const PRELOADER_MIN_MS = 200;
/**
 * Hard ceiling on the preloader regardless of what the network is doing. The
 * overlay is opaque and covers the viewport, and an element under an opaque
 * overlay is not an LCP candidate — so anything that can keep it mounted is a
 * direct tax on LCP.
 */
const PRELOADER_MAX_MS = 1200;

function AppShell() {
  const [minimumDelayDone, setMinimumDelayDone] = useState(false);

  const seoQuery = useQuery<PublicRuntimeSeo>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo", { cache: "no-store" });
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const preloaderEnabled = useMemo(() => {
    const serverValue = seoQuery.data?.preloaderEnabled;
    if (typeof serverValue === "boolean") return serverValue;
    if (typeof window === "undefined") return true;
    try {
      const stored = localStorage.getItem("fx.preloaderEnabled");
      if (stored === "0") return false;
      if (stored === "1") return true;
    } catch {
      // ignore
    }
    return true;
  }, [seoQuery.data?.preloaderEnabled]);

  useEffect(() => {
    const min = window.setTimeout(() => setMinimumDelayDone(true), PRELOADER_MIN_MS);
    const max = window.setTimeout(() => setMinimumDelayDone(true), PRELOADER_MAX_MS);
    return () => {
      window.clearTimeout(min);
      window.clearTimeout(max);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof seoQuery.data?.preloaderEnabled !== "boolean") return;
    try {
      localStorage.setItem("fx.preloaderEnabled", seoQuery.data.preloaderEnabled ? "1" : "0");
    } catch {
      // ignore
    }
  }, [seoQuery.data?.preloaderEnabled]);

  /**
   * The gate used to be:
   *   `preloaderEnabled && (!windowLoaded || seoQuery.isLoading || !minimumDelayDone)`
   *
   * `windowLoaded` was the killer. It resolves on the `load` event, which waits
   * for *every* subresource — including the 948 KiB sidebar promo image that the
   * mobile run flagged. So an opaque full-screen overlay covered the page until
   * the slowest image on the site had finished downloading, and the hero could
   * not be painted (or counted as the LCP element) before that.
   *
   * `seoQuery.isLoading` was the second cost: it put a `/api/seo` round trip in
   * front of first paint. The query is still used — it is how an admin's
   * "preloader off" setting reaches the browser — but it now *hides* an
   * already-rendered preloader instead of gating whether the app renders at all.
   *
   * What is left is a short cosmetic delay with a hard ceiling: the overlay is
   * gone within PRELOADER_MAX_MS no matter how slow the network is.
   *
   * If first paint matters more than the brand moment, turn the preloader off
   * entirely in Admin → Settings; it is a deliberate cost, not a free one.
   */
  const showPreloader = preloaderEnabled && !minimumDelayDone;

  return (
    <>
      {showPreloader ? (
        <Preloader faviconUrl={seoQuery.data?.faviconUrl} siteName={seoQuery.data?.siteName} />
      ) : null}
      <Router />
    </>
  );
}

export default App;
